export type LengthUnit = "cm" | "mm" | "inch";

export const LENGTH_UNITS = ["cm", "mm", "inch"] as const;
export const LENGTH_UNIT_CHANGED_EVENT = "length-unit-changed";
export const LENGTH_UNIT_STORAGE_KEY = "sugartech:length-unit";

const INCH_PER_M = 39.37007874015748;

export function isLengthUnit(value: unknown): value is LengthUnit {
  return value === "cm" || value === "mm" || value === "inch";
}

function readStored(): LengthUnit {
  try {
    const raw = localStorage.getItem(LENGTH_UNIT_STORAGE_KEY);
    if (isLengthUnit(raw)) return raw;
  } catch {
    /* ignore quota / private mode */
  }
  return "cm";
}

class LengthUnitStore extends EventTarget {
  private _unit: LengthUnit = readStored();

  get unit(): LengthUnit {
    return this._unit;
  }

  setUnit(unit: LengthUnit): void {
    if (this._unit === unit) return;
    this._unit = unit;
    try {
      localStorage.setItem(LENGTH_UNIT_STORAGE_KEY, unit);
    } catch {
      /* ignore quota / private mode */
    }
    this.dispatchEvent(
      new CustomEvent<LengthUnit>(LENGTH_UNIT_CHANGED_EVENT, { detail: unit }),
    );
    window.dispatchEvent(
      new CustomEvent<LengthUnit>(LENGTH_UNIT_CHANGED_EVENT, { detail: unit }),
    );
  }
}

export const lengthUnitStore = new LengthUnitStore();

export function getLengthUnit(): LengthUnit {
  return lengthUnitStore.unit;
}

export function metersToDisplay(
  meters: number,
  unit: LengthUnit = getLengthUnit(),
): number {
  if (unit === "mm") return meters * 1000;
  if (unit === "inch") return meters * INCH_PER_M;
  return meters * 100;
}

export function displayToMeters(
  value: number,
  unit: LengthUnit = getLengthUnit(),
): number {
  if (unit === "mm") return value / 1000;
  if (unit === "inch") return value / INCH_PER_M;
  return value / 100;
}

export function formatLengthValue(
  meters: number,
  unit: LengthUnit = getLengthUnit(),
): string {
  const n = metersToDisplay(meters, unit);
  if (unit === "mm") {
    return String(Math.round(n));
  }
  const text = n.toFixed(1);
  return text.endsWith(".0") ? text.slice(0, -2) : text;
}

export function unitSuffix(unit: LengthUnit = getLengthUnit()): string {
  return unit === "inch" ? "in" : unit;
}

export function formatLength(
  meters: number,
  unit: LengthUnit = getLengthUnit(),
): string {
  return `${formatLengthValue(meters, unit)} ${unitSuffix(unit)}`;
}

/** Parse a typed label into meters. Returns null when the number is missing. */
export function parseLengthInput(
  raw: string,
  unit: LengthUnit = getLengthUnit(),
): number | null {
  const cleaned = raw.replace(/,/g, ".").replace(/[^\d.+-]/g, "");
  const n = Number.parseFloat(cleaned);
  if (!Number.isFinite(n)) return null;
  return displayToMeters(n, unit);
}
